import React, { useState, useEffect } from 'react';
import { useSnackbar } from '../../context/SnackbarContext';
import {
  PaintBrushIcon,
  DocumentTextIcon,
  AdjustmentsHorizontalIcon,
  SunIcon,
  MoonIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { injectGlobalStyles, DEFAULT_SETTINGS } from '../../utils/themeUtils';
import LayoutStylePicker from '../ui/LayoutStylePicker';
import ApiLoader from '../ui/ApiLoader';

const PlatformSettings = ({ user, loading = false }) => {
  const defaultPlatformSettings = DEFAULT_SETTINGS;
  const [platformSettings, setPlatformSettings] = useState(defaultPlatformSettings);
  const [savingSettings, setSavingSettings] = useState(false);
  const { showSnackbar } = useSnackbar();
  const isDark = platformSettings.appearance?.theme === 'dark';

  const fontFamilies = [
    { name: 'Inter', value: 'Inter', category: 'Sans Serif' },
    { name: 'Roboto', value: 'Roboto', category: 'Sans Serif' },
    { name: 'Open Sans', value: 'Open Sans', category: 'Sans Serif' },
    { name: 'Lato', value: 'Lato', category: 'Sans Serif' },
    { name: 'Montserrat', value: 'Montserrat', category: 'Sans Serif' },
    { name: 'Poppins', value: 'Poppins', category: 'Sans Serif' },
    { name: 'Source Sans Pro', value: 'Source Sans Pro', category: 'Sans Serif' },
    { name: 'Georgia', value: 'Georgia', category: 'Serif' },
    { name: 'Times New Roman', value: 'Times New Roman', category: 'Serif' },
    { name: 'Monaco', value: 'Monaco', category: 'Monospace' },
    { name: 'Menlo', value: 'Menlo', category: 'Monospace' }
  ];

  const colorPalettes = [
    { name: 'Brand', value: '#26256b', rgb: '38, 37, 107' },
    { name: 'Blue', value: '#3B82F6', rgb: '59, 130, 246' },
    { name: 'Navy', value: '#1E3A5F', rgb: '30, 58, 95' },
    { name: 'Steel Blue', value: '#3D6B99', rgb: '61, 107, 153' },
    { name: 'Sapphire', value: '#2E4374', rgb: '46, 67, 116' },
    { name: 'Plum', value: '#5B3A6B', rgb: '91, 58, 107' },
  ];

  const applyLivePreview = (setting, value) => {
    // Get current settings from localStorage
    const currentSettings = JSON.parse(localStorage.getItem('platformSettings') || JSON.stringify(defaultPlatformSettings));

    switch (setting) {
      case 'theme':
        currentSettings.appearance = currentSettings.appearance || {};
        currentSettings.appearance.theme = value;
        currentSettings.layout = currentSettings.layout || defaultPlatformSettings.layout;
        break;
      case 'primaryColor':
        currentSettings.appearance = currentSettings.appearance || {};
        currentSettings.appearance.primaryColor = value;
        currentSettings.layout = currentSettings.layout || defaultPlatformSettings.layout;
        break;
      case 'layoutStyle':
        currentSettings.appearance = currentSettings.appearance || {};
        currentSettings.appearance.layoutStyle = value;
        currentSettings.layout = currentSettings.layout || defaultPlatformSettings.layout;
        break;
      case 'fontFamily':
      case 'fontSize':
      case 'fontWeight':
      case 'lineHeight':
      case 'letterSpacing':
      case 'borderRadius':
        currentSettings.appearance = currentSettings.appearance || {};
        currentSettings.appearance[setting] = value;
        currentSettings.layout = currentSettings.layout || defaultPlatformSettings.layout;
        break;
      case 'sidebarWidth':
      case 'contentPadding':
        currentSettings.layout = currentSettings.layout || {};
        currentSettings.layout[setting] = value;
        currentSettings.appearance = currentSettings.appearance || defaultPlatformSettings.appearance;
        break;
      default:
        return;
    }

    // Save updated settings to localStorage
    localStorage.setItem('platformSettings', JSON.stringify(currentSettings));

    // Re-inject global styles with updated settings
    injectGlobalStyles(currentSettings);

    // Dispatch event to notify Layout and other components immediately
    window.dispatchEvent(new CustomEvent('settingsChanged', { detail: { settings: currentSettings } }));
  };

  const handleSettingChange = (category, setting, value) => {
    setPlatformSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));

    // Apply live preview
    applyLivePreview(setting, value);
  };

  const handleSavePlatformSettings = async () => {
    setSavingSettings(true);
    try {
      // Save to localStorage
      localStorage.setItem('platformSettings', JSON.stringify(platformSettings));

      // Apply global styles immediately
      injectGlobalStyles(platformSettings);

      // Dispatch custom event to notify other components (like Layout)
      window.dispatchEvent(new CustomEvent('settingsChanged', { detail: { settings: platformSettings } }));

      // Optionally save to backend
      // await axios.post(`${BACKEND_URL}/api/platform-settings/`, platformSettings);

      showSnackbar('Settings saved and applied!', 'success');
    } catch (error) {
      console.error('Error saving platform settings:', error);
      showSnackbar('Failed to save settings', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleResetPlatformSettings = () => {
    setPlatformSettings(defaultPlatformSettings);

    // Save to localStorage
    localStorage.setItem('platformSettings', JSON.stringify(defaultPlatformSettings));

    // Apply global styles with default settings
    injectGlobalStyles(defaultPlatformSettings);

    // Dispatch event to notify Layout and other components immediately
    window.dispatchEvent(new CustomEvent('settingsChanged', { detail: { settings: defaultPlatformSettings } }));

    showSnackbar('Settings reset to defaults', 'success');
  };
  useEffect(() => {
    // Load platform settings from localStorage or backend on mount
    const loadPlatformSettings = async () => {
      try {
        const savedSettings = localStorage.getItem('platformSettings');
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          // Migrate old sidebarWidth (256) to new default (224)
          if (parsed.layout && parsed.layout.sidebarWidth === 256) {
            parsed.layout.sidebarWidth = 224;
            localStorage.setItem('platformSettings', JSON.stringify(parsed));
          }
          setPlatformSettings(parsed);
          injectGlobalStyles(parsed);
        } else {
          // Optionally load from backend
          // const response = await axios.get(`${BACKEND_URL}/api/platform-settings/`);
          // setPlatformSettings(response.data);
          // injectGlobalStyles(response.data);

          // Use defaults and apply them
          injectGlobalStyles(defaultPlatformSettings);
        }
      } catch (error) {
        console.error('Error loading platform settings:', error);
        // Use defaults
        injectGlobalStyles(defaultPlatformSettings);
      }
    };

    loadPlatformSettings();
  }, []);

  const activeColor = platformSettings.appearance.primaryColor;

  if (loading) {
    return <ApiLoader label="Loading platform settings..." className="py-16" />;
  }

  return (
   <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Platform Settings</h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Customize the look and feel of your workspace</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleResetPlatformSettings}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark ? 'text-gray-300 bg-gray-800 border-gray-600 hover:bg-gray-700' : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'}`}
          >
            Reset Defaults
          </button>
          <button
            onClick={handleSavePlatformSettings}
            disabled={savingSettings}
            className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-all hover:shadow-lg disabled:opacity-50"
            style={{ backgroundColor: activeColor }}
          >
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Appearance Settings */}
        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
          <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-100 bg-gray-50/50'}`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${activeColor}15` }}>
                <PaintBrushIcon className="w-5 h-5" style={{ color: activeColor }} />
              </div>
              <div>
                <h3 className={`text-base font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Appearance & Theming</h3>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Configure theme mode and brand color</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Theme Toggle */}
              <div>
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Theme Mode</label>
                <div className="flex gap-3">
                  {[
                    { id: 'light', label: 'Light', icon: <SunIcon className="w-5 h-5" /> },
                    { id: 'dark', label: 'Dark', icon: <MoonIcon className="w-5 h-5" /> },
                  ].map((theme) => {
                    const isSelected = platformSettings.appearance.theme === theme.id;
                    return (
                      <button
                        key={theme.id}
                        onClick={() => handleSettingChange('appearance', 'theme', theme.id)}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-current shadow-sm'
                            : isDark
                              ? 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500 hover:bg-gray-600'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        style={isSelected ? { borderColor: activeColor, backgroundColor: `${activeColor}08`, color: activeColor } : {}}
                      >
                        {theme.icon}
                        <span className="font-medium text-sm">{theme.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Color Palette */}
              <div>
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Brand Color</label>
                <div className="grid grid-cols-6 gap-2">
                  {colorPalettes.map((color) => {
                    const isSelected = platformSettings.appearance.primaryColor === color.value;
                    return (
                      <button
                        key={color.value}
                        onClick={() => handleSettingChange('appearance', 'primaryColor', color.value)}
                        className="group relative w-full aspect-square rounded-xl transition-all hover:scale-110"
                        style={{
                          backgroundColor: color.value,
                          boxShadow: isSelected
                            ? `0 0 0 3px white, 0 0 0 5px ${color.value}`
                            : '0 1px 3px rgba(0,0,0,0.12)',
                        }}
                        title={color.name}
                      >
                        {isSelected && (
                          <svg className="w-4 h-4 text-white absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layout Style Settings */}
        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
          <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-100 bg-gray-50/50'}`}>
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${activeColor}15` }}>
                <Squares2X2Icon className="w-5 h-5" style={{ color: activeColor }} />
              </div>
              <div>
                <h3 className={`text-base font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Layout Style</h3>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Choose a layout preset for sidebar, header, and content area</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <LayoutStylePicker
              selectedStyle={platformSettings.appearance?.layoutStyle || 'classic'}
              onSelect={(styleId) => handleSettingChange('appearance', 'layoutStyle', styleId)}
              primaryColor={activeColor}
            />
          </div>
        </div>

        {/* Typography Settings */}
        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
          <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-100 bg-gray-50/50'}`}>
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
                <DocumentTextIcon className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
              </div>
              <div>
                <h3 className={`text-base font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Typography & Fonts</h3>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Fine-tune text rendering across the platform</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Font Family */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Font Family</label>
                <select
                  value={platformSettings.appearance.fontFamily}
                  onChange={(e) => handleSettingChange('appearance', 'fontFamily', e.target.value)}
                  className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:border-transparent transition-shadow ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-200'}`}
                  style={{ focusRingColor: activeColor }}
                >
                  {fontFamilies.map((font) => (
                    <option key={font.value} value={font.value}>
                      {font.name} ({font.category})
                    </option>
                  ))}
                </select>
              </div>

              {/* Font Size */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Font Size</label>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{platformSettings.appearance.fontSize}px</span>
                </div>
                <input
                  type="range"
                  min="12"
                  max="18"
                  value={platformSettings.appearance.fontSize}
                  onChange={(e) => handleSettingChange('appearance', 'fontSize', parseInt(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-current"
                  style={{ accentColor: activeColor }}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>12px</span>
                  <span>15px</span>
                  <span>18px</span>
                </div>
              </div>

              {/* Font Weight */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Font Weight</label>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{platformSettings.appearance.fontWeight}</span>
                </div>
                <input
                  type="range"
                  min="300"
                  max="700"
                  step="100"
                  value={platformSettings.appearance.fontWeight}
                  onChange={(e) => handleSettingChange('appearance', 'fontWeight', parseInt(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: activeColor }}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Light</span>
                  <span>Regular</span>
                  <span>Bold</span>
                </div>
              </div>

              {/* Line Height */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Line Height</label>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{platformSettings.appearance.lineHeight}</span>
                </div>
                <input
                  type="range"
                  min="1.2"
                  max="2.0"
                  step="0.1"
                  value={platformSettings.appearance.lineHeight}
                  onChange={(e) => handleSettingChange('appearance', 'lineHeight', parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: activeColor }}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1.2</span>
                  <span>1.6</span>
                  <span>2.0</span>
                </div>
              </div>

              {/* Letter Spacing */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Letter Spacing</label>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{platformSettings.appearance.letterSpacing}em</span>
                </div>
                <input
                  type="range"
                  min="-0.05"
                  max="0.1"
                  step="0.005"
                  value={platformSettings.appearance.letterSpacing}
                  onChange={(e) => handleSettingChange('appearance', 'letterSpacing', parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: activeColor }}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>-0.05em</span>
                  <span>0em</span>
                  <span>0.1em</span>
                </div>
              </div>

              {/* Border Radius */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Border Radius</label>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{platformSettings.appearance.borderRadius}px</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="16"
                  value={platformSettings.appearance.borderRadius}
                  onChange={(e) => handleSettingChange('appearance', 'borderRadius', parseInt(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: activeColor }}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Square</span>
                  <span>Rounded</span>
                  <span>Pill</span>
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="mt-8">
              <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Live Preview</h4>
              <div
                className={`p-6 rounded-xl border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                style={{
                  fontFamily: platformSettings.appearance.fontFamily,
                  fontSize: `${platformSettings.appearance.fontSize}px`,
                  fontWeight: platformSettings.appearance.fontWeight,
                  lineHeight: platformSettings.appearance.lineHeight,
                  letterSpacing: `${platformSettings.appearance.letterSpacing}em`,
                }}
              >
                <h5 className="text-lg font-semibold mb-2" style={{ color: activeColor }}>
                  Sample Heading
                </h5>
                <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  This is a sample paragraph demonstrating how your typography settings will appear across the platform.
                  Adjust font family, size, weight, and spacing in real-time.
                </p>
                <div className="flex gap-3">
                  <button
                    className="px-4 py-2 text-white text-sm font-medium transition-all"
                    style={{
                      backgroundColor: activeColor,
                      borderRadius: `${platformSettings.appearance.borderRadius}px`
                    }}
                  >
                    Primary Action
                  </button>
                  <button
                    className="px-4 py-2 text-sm font-medium border transition-all"
                    style={{
                      color: activeColor,
                      borderColor: activeColor,
                      borderRadius: `${platformSettings.appearance.borderRadius}px`
                    }}
                  >
                    Secondary
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layout Settings */}
        {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <AdjustmentsHorizontalIcon className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Layout & Spacing</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Sidebar Width: {platformSettings.layout?.sidebarWidth || defaultPlatformSettings.layout.sidebarWidth}px
            </label>
            <input
              type="range"
              min="200"
              max="320"
              step="8"
              value={platformSettings.layout?.sidebarWidth || defaultPlatformSettings.layout.sidebarWidth}
              onChange={(e) => handleSettingChange('layout', 'sidebarWidth', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Content Padding: {platformSettings.layout?.contentPadding || defaultPlatformSettings.layout.contentPadding}px
            </label>
            <input
              type="range"
              min="16"
              max="48"
              step="4"
              value={platformSettings.layout?.contentPadding || defaultPlatformSettings.layout.contentPadding}
              onChange={(e) => handleSettingChange('layout', 'contentPadding', parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      </div> */}

        {/* Actions */}
      </div>
    </div>
  );
};

export default PlatformSettings;
